import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Phone, Mail, Clock, Facebook, Youtube, MessageCircle, Globe } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "/logo-gandiaye-md.webp";

const quickLinks = [
  { name: 'Accueil', path: '/' },
  { name: 'Ma Mairie', path: '/mairie' },
  { name: 'Découvrir Gandiaye', path: '/decouvrir' },
  { name: 'Vie Quotidienne', path: '/vie-quotidienne' },
  { name: 'Projets', path: '/projets' },
  { name: 'Actualités', path: '/medias' },
  { name: 'Contact', path: '/contact' },
];

const serviceLinks = [
  { name: 'État Civil', path: '/mairie' },
  { name: 'Urbanisme', path: '/mairie' },
  { name: 'Éducation', path: '/vie-quotidienne' },
  { name: 'Santé', path: '/vie-quotidienne' },
  { name: 'Documents', path: '/documents' },
];

const DEFAULTS = {
  contact_info: { address: 'Mairie de Gandiaye, Route Nationale N°1, Sénégal', phone: '+221 33 949 10 10', email: 'mairie.gandiaye@gmail.com', hours: 'Lun - Ven: 8h00 - 17h00' },
  social_links: [{ title: 'Facebook', url: 'https://facebook.com/bennobokkyaakaarGandiaye' }],
};

export const Footer = () => {
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    axios.get(`${API}/settings`).then(res => setSettings(res.data)).catch(() => {});
  }, []);

  const contact = settings?.contact_info || DEFAULTS.contact_info;
  const socialLinks = Array.isArray(settings?.social_links) ? settings.social_links : DEFAULTS.social_links;

  return (
    <footer>
      <div className="h-2 bg-gradient-to-r from-[#0f6b45] via-[#FCD116] to-[#CE1126]" />
      
      <div className="bg-[#0a5535] text-white py-12">
        <div className="container-main">
          <div className="grid md:grid-cols-4 gap-8">
            
            {/* Logo & Info */}
            <div>
              <div className="flex items-center gap-3 mb-4">
                <img src={LOGO_URL} alt="Gandiaye" className="h-14 w-14 rounded-full bg-white p-0.5" />
                <div>
                  <h3 className="font-bold text-lg">Gandiaye</h3>
                  <p className="text-white/60 text-sm">Commune du Sénégal</p>
                </div>
              </div>
              <p className="text-white/70 text-sm leading-relaxed">
                Le carrefour de l'émergence, la force de la Teranga. Bienvenue sur le portail officiel.
              </p>
              <div className="flex gap-3 mt-4">
                {socialLinks.filter(l => l.url).map((link, i) => (
                  <a key={i} href={link.url} target="_blank" rel="noopener noreferrer" title={link.title} className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center hover:bg-white/20 transition-colors" data-testid={`footer-social-${i}`}>
                    {link.url.includes('facebook') ? <Facebook size={18} /> :
                     link.url.includes('youtube') ? <Youtube size={18} /> :
                     link.url.includes('wa.me') || link.url.includes('whatsapp') ? <MessageCircle size={18} /> :
                     <Globe size={18} />}
                  </a>
                ))}
              </div>
            </div>

            {/* Quick Links */}
            <div>
              <h4 className="font-bold mb-4">Liens Rapides</h4>
              <ul className="space-y-2">
                {quickLinks.map((link) => (
                  <li key={link.path}>
                    <Link to={link.path} className="text-white/70 hover:text-white text-sm transition-colors">
                      {link.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Services */}
            <div>
              <h4 className="font-bold mb-4">Démarches</h4>
              <ul className="space-y-2">
                {serviceLinks.map((service, i) => (
                  <li key={i}>
                    <Link to={service.path} className="text-white/70 hover:text-white text-sm transition-colors">
                      {service.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Contact */}
            <div data-testid="footer-contact">
              <h4 className="font-bold mb-4">Contact</h4>
              <ul className="space-y-3 text-sm">
                <li className="flex items-start gap-3">
                  <MapPin size={18} className="text-[#FCD116] flex-shrink-0 mt-0.5" />
                  <span className="text-white/70">{contact.address}</span>
                </li>
                <li className="flex items-center gap-3">
                  <Phone size={18} className="text-[#FCD116]" />
                  <a href={`tel:${contact.phone?.replace(/\s/g, '')}`} className="text-white/70 hover:text-white transition-colors">{contact.phone}</a>
                </li>
                <li className="flex items-center gap-3">
                  <Mail size={18} className="text-[#FCD116]" />
                  <a href={`mailto:${contact.email}`} className="text-white/70 hover:text-white transition-colors">{contact.email}</a>
                </li>
                <li className="flex items-center gap-3">
                  <Clock size={18} className="text-[#FCD116]" />
                  <span className="text-white/70">{contact.hours}</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-[#063d28] py-4">
        <div className="container-main text-center text-white/60 text-sm">
          © {new Date().getFullYear()} Commune de Gandiaye. Tous droits réservés.
        </div>
      </div>
    </footer>
  );
};

export default Footer;
